import type { Role } from "./api";
import { settingsSectionPaths } from "./settingsSections";

// The home route for a role: engineers land in the mobile PWA, external auditors in
// the read-only compliance portal, everyone else in the office desktop app.
export function homeFor(role: Role): string {
  if (role === "engineer") return "/engineer";
  if (role === "auditor") return "/auditor/compliance-reports";
  return "/office";
}

// ---- Office sidebar navigation ----
// Grouped, labelled sections rendered by the collapsible left sidebar. Defined as
// pure data (no JSX) so it is the single source of truth shared by the layout and
// its test, and so new sections/items bolt on without touching the layout. The
// `icon` is a short glyph shown when the sidebar is collapsed to a rail.
export interface NavItem {
  to: string;
  label: string;
  icon: string; // short label used in the collapsed icon rail
  end?: boolean; // exact-match active state (the dashboard index route)
  // A plain-English tooltip for labels that presume domain knowledge (Session AAB, Item 4:
  // "PPM" means nothing to a new user). Rendered as the link's title; omitted = the label.
  hint?: string;
  // P-1, Session AQT: hides the item from an office-role login (never a NEW restriction -- only
  // set on items whose page ALREADY refuses a non-admin, server-side at minimum, so this makes
  // the menu honest about an existing boundary rather than inventing one). Deliberately a small,
  // named allow-list, not a wholesale role system: the office shell is reachable only by admin
  // and office (ProtectedRoute), so the one real distinction needed here is binary.
  adminOnly?: boolean;
  // SC7A: the pages this hub holds, in tab order. The first tab's `to` is this item's own `to`.
  // An item with no tabs is a plain link (Call-outs).
  tabs?: NavTab[];
}

// One page inside a hub (Session SC7A). A tab is a link to an EXISTING route, so the page component is
// reused unchanged and its old URL keeps working. `to` may carry a query (the Ready to send tab is
// /office/jobsheets/release?stage=ready_to_send); the tab is active when the path and every query key it
// names match.
export interface NavTab {
  to: string;
  label: string;
  // Plain-English words for the menu search: put the OLD name of a moved page here so it stays findable.
  hint?: string;
  // Pages that belong to this tab but are reached from inside it (path or path prefix): they keep the
  // hub lit and show its tab strip. Never affects what is searchable.
  also?: string[];
  adminOnly?: boolean;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

// ---- THE CALM MENU (Session SC7A, the Director's layout of 22 October 2026) ----
//
// FOURTEEN top-level items in FOUR quiet groups, down from 50 items in seven groups (Session AOA's
// seven-group menu, then ATB, AUB and BDF's consolidations, is in git history and in
// docs/reports/CALM-SCREENS-BEFORE.md). THIS FILE IS THE ONE PLACE that decides what is in the menu: to
// put any item back, add it here (a top-level NavItem, or a NavTab under its hub) and nothing else
// needs to change, because every old page still has its own route in src/App.tsx.
//
// HOW IT WORKS. A top-level item is a HUB. Its `to` is its first tab's path, so clicking it opens
// that page, and its `tabs` are the other pages it holds. A tab is a plain link to an EXISTING route
// (the page component is reused unchanged), so every old URL still opens the same page, and the hub's
// tab strip (src/components/HubTabs.tsx, drawn by OfficeLayout above the page) shows where it is. A
// tab strip appears only on a tab's own page, never on a record page (a customer, a quote), so a
// record is never crowded. A tab may carry `also` paths: pages that belong to it but are reached by a
// link inside it (the Billing hub's invoices, the Compliance overview's registers), so the hub stays
// lit on them.
//
// NOTHING IS LOST, and the tests hold it: no route is deleted (App.routes.test.tsx), every tab path
// resolves to a registered route, every tab is found by the menu search (src/lib/navSearch.ts indexes
// the tab label, hint and path, so an old name written into a hint still finds the page), adminOnly
// stays on exactly the tabs that had it, and a person's saved layout from before still loads
// (applyNavLayout drops what it does not know and appends the rest to its default group).
//
// THE COUNT IS LOCKED by nav.test.ts: no more than 14 top-level items and exactly 4 groups. Add a new
// page as a TAB on the hub where the person already is, never as a fifteenth item (CLAUDE.md, the calm
// screen rule; a new top-level item needs a Director decision).
//
// Pages with no menu entry at all (reached only from a link inside a hub, as before): the Compliance
// overview's Accidents, Complaints, Enforcement, Environmental records, Internal audits and Management
// reviews cards (D-SC7A-1: they stay exactly where they were, behind the Compliance overview).
//
// Deliberately UNROUTED, not deleted (Director, 17 Aug 2026): Call log and Van stock, kept for a future
// white-label product. RFID tag management lives on the lift record.
export const OFFICE_NAV: NavSection[] = [
  {
    // THE WORKING DAY, in the order it runs: read what needs you, see the diary, take the calls, clear
    // the sheets that came back, deal with what they raised, then the statutory examinations.
    label: "Work",
    items: [
      {
        to: "/office",
        label: "Today",
        icon: "DB",
        end: true,
        hint: "Your launchpad: today's numbers, the quick actions, and what is outstanding. Also shows who is on standby this week and next. Tabs: Dashboard, Priorities and analytics",
        tabs: [
          { to: "/office", label: "Dashboard", hint: "Your launchpad: today's numbers, the quick actions, and what is outstanding. Also shows who is on standby this week and next" },
          { to: "/office/reporting", label: "Priorities", hint: "Today's priorities: the ranked list of what needs doing, plus the monthly business figures. Previously called Business analytics, Priorities and analytics" },
        ],
      },
      {
        to: "/office/scheduling",
        label: "Schedule",
        icon: "MS",
        hint: "The diary and every job. Tabs: Calendar, All jobs, Due for service, Map. Previously Scheduling",
        tabs: [
          { to: "/office/scheduling", label: "Calendar", hint: "The master forward calendar: every visit scheduled in, by day, week or month, and who is attending it. Previously called Scheduling" },
          { to: "/office/jobs", label: "All jobs", hint: "Every job of every type in one list: filter by type, status (on hold for parts included), engineer, customer and date, so a job is found without knowing which menu it lives under" },
          { to: "/office/ppm", label: "Due for service", hint: "Planned preventative maintenance (PPM): every lift banded by how soon its next service visit is due. Previously called Service schedule" },
          { to: "/office/map", label: "Map", hint: "The whole lift portfolio on a map, for a geography or routing question" },
        ],
      },
      {
        to: "/office/callouts",
        label: "Call-outs",
        icon: "CO",
        hint: "Log a call-out, dispatch an engineer, and watch the response clock against the promised time",
      },
      {
        to: "/office/jobsheets",
        label: "Servicing",
        icon: "SR",
        hint: "Planned service visits from sheet to customer. Tabs: Service records, Awaiting release, Ready to send, Sent today, Knowledge base. Previously the Release and send queue",
        tabs: [
          { to: "/office/jobsheets", label: "Service records", hint: "Job sheets the engineers have sent back for planned service and PPM visits. A call-out's visit record is approved on the Call-outs page instead" },
          { to: "/office/jobsheets/release", label: "Awaiting release", hint: "Closed visits waiting for the office to check and release in one go. Previously the Release and send queue" },
          { to: "/office/jobsheets/release?stage=ready_to_send", label: "Ready to send", hint: "Released visit reports waiting to be sent to the customer. Previously the Release and send queue" },
          { to: "/office/jobsheets/release?stage=sent_today", label: "Sent today", hint: "Reports sent today, from Guvnor or recorded as sent from Gmail, with the way back for a record entered by mistake" },
          { to: "/office/knowledge", label: "Knowledge base", hint: "The technical library for the portfolio: manuals, drawings, previous reports and emailed attachments, tagged by site, lift, manufacturer and model. Upload documents here and ask it a question" },
        ],
      },
      {
        to: "/office/defects",
        label: "Repairs",
        icon: "DF",
        hint: "Faults found, the repair work they raise, and its estimates. Tabs: To fix (Defects), Repair jobs, Repair estimates, Projects",
        tabs: [
          { to: "/office/defects", label: "To fix", hint: "Every fault waiting to be fixed, wherever it was found (a service visit, a call-out, a LOLER report or an insurer), with its source and what has happened to it so far. Previously called Defects" },
          { to: "/office/repair-jobs", label: "Repair jobs", hint: "The open repair and technician visit jobs: who is booked, when, and what is on hold" },
          { to: "/office/repair-estimates", label: "Repair estimates", hint: "Every repair estimate: draft, sent, approved or declined, with the actions to send, record an answer and raise the job" },
          { to: "/office/projects", label: "Projects", hint: "Repair and modernisation work: quoted, signed, delivered and invoiced" },
        ],
      },
      {
        to: "/office/loler-due",
        label: "LOLER and SAFed",
        icon: "LO",
        hint: "Statutory examinations and supplementary testing. Tabs: Due dates, LOLER reports (with To close: the insurer items, insurance items or examiner items from those reports), SAFed tests",
        tabs: [
          { to: "/office/loler-due", label: "Due dates", hint: "Every lift subject to LOLER with its next thorough examination due, who arranges it (the customer, or ELE where recorded) and the red, amber or green band, soonest first" },
          { to: "/office/loler", label: "LOLER reports", hint: "Statutory thorough-examination reports received from the examining body, and the remedial work they raise. Tabs inside: Reports, and To close (the insurer items, insurance items or examiner items from those reports, worked to closure)", also: ["/office/insurer-items"] },
          { to: "/office/safed", label: "SAFed tests", hint: "Supplementary testing of lifts: every recommendation, quotation, booked job and result, and the button to raise a SAFed test" },
        ],
      },
    ],
  },
  {
    label: "Sales and customers",
    items: [
      {
        to: "/office/quotes",
        label: "Quotes",
        icon: "QT",
        hint: "Maintenance quotes and the portfolio discount. Tabs: Quotes, Quote portfolios",
        tabs: [
          { to: "/office/quotes", label: "Quotes" },
          { to: "/office/quote-portfolios", label: "Quote portfolios", hint: "Group several site quotes for one customer and roll them up with the portfolio discount" },
        ],
      },
      {
        to: "/office/customers",
        label: "Customers",
        icon: "CU",
        hint: "Everyone you serve and what they hold. Tabs: Customers, Sites, Lifts, Contracts, Renewals, Portal logins, Portal documents, Customer satisfaction",
        tabs: [
          { to: "/office/customers", label: "Customers" },
          { to: "/office/sites", label: "Sites" },
          { to: "/office/lifts", label: "Lifts" },
          { to: "/office/contracts", label: "Contracts" },
          { to: "/office/renewals", label: "Renewals", hint: "Renewal proposals to confirm, skip or issue as a link to the customer. A renewal changes a contract" },
          { to: "/office/portal-users", label: "Portal logins", hint: "Client Portal logins: customer logins for the Client Portal: create, invite and disable them" },
          { to: "/office/company-documents", label: "Portal documents", hint: "Client Portal documents: company documents published to every Client Portal customer: H&S policy, insurance, RAMS, COSHH, test annexes. Previously called Company documents" },
          { to: "/office/customer-satisfaction", label: "Customer satisfaction", hint: "Send a satisfaction survey (Net Promoter Score) to a customer by hand. The survey figures are under Reports" },
        ],
      },
      {
        to: "/office/billing",
        label: "Money",
        icon: "BL",
        hint: "Invoices, aged debtors, PPM payment state, contract allowances and purchase orders. Previously Billing, Invoices, Aged debtors, Allowances, PPM payments",
        tabs: [
          {
            to: "/office/billing",
            label: "Billing",
            hint: "Invoices, aged debtors, PPM payment state and contract allowances, one home for the money side of the business. Previously four separate items: Invoices, Aged debtors, Allowances, PPM payments",
            also: ["/office/invoices", "/office/debtors", "/office/ppm-payments", "/office/allowances"],
          },
          { to: "/office/purchase-orders", label: "Purchase orders", hint: "Orders raised with suppliers for parts, and what has arrived into each engineer's van" },
        ],
      },
      {
        to: "/office/mail",
        label: "Mail",
        icon: "ML",
        hint: "Every email in one place, and the messages to your engineers. Tabs: Mail, Engineer messages, Reply templates. Previously Email engine, Enquiries, Emails to answer (Inbox triage) and Work orders",
        tabs: [
          { to: "/office/mail", label: "Mail", hint: "Every email in one place: replies ready to check and send, ones that need a person, work orders, filed mail and calls from unknown callers. Previously Email engine, Enquiries, Emails to answer (Inbox triage) and Work orders", also: ["/office/enquiries", "/office/email-engine", "/office/inbox-triage", "/office/work-orders"] },
          { to: "/office/messages", label: "Engineer messages", hint: "One-to-one messages with each engineer. Mirrors what the engineer sees on their own phone. Previously called Messages" },
          { to: "/office/reply-templates", label: "Reply templates", hint: "The approved wording the email engine fills when it drafts a reply, the proposals waiting for a decision, and the reply style guide" },
        ],
      },
    ],
  },
  {
    label: "Team",
    items: [
      {
        to: "/office/engineers",
        label: "People",
        icon: "EN",
        hint: "Everyone who does the work and their paperwork. Tabs: Engineers, Timesheets, Standby rota, Leave, Expenses, Lone worker safety, Staff certificates, Subcontractors, Training sessions",
        tabs: [
          { to: "/office/engineers", label: "Engineers", hint: "Engineer records and logins" },
          { to: "/office/timesheets", label: "Timesheets", hint: "Weekly hours and pay per engineer, from the times recorded on each visit (admin only)" },
          { to: "/office/standby", label: "Standby", hint: "Standby rota: who is on call each week, set in advance on a repeating rotation, with any single week changeable when somebody is away" },
          { to: "/office/leave", label: "Leave", hint: "Recorded holiday, sick leave and other time off for each engineer, that the standby rota and dispatch warn against" },
          { to: "/office/expenses", label: "Expenses", hint: "Mileage, materials and other costs engineers claim: review, approve or reject, filtered by engineer, status or date" },
          { to: "/office/lone-worker", label: "Lone worker", hint: "Lone worker safety: who is checked in on site right now, who has missed a safety check-in, and the record of the visits that have finished" },
          { to: "/office/personnel", label: "Staff certificates", hint: "Both people registers on one page: personnel compliance documents (DBS, data protection) on the Office tab, engineer compliance (certifications and competence) on the Engineer tab. Previously called Personnel" },
          { to: "/office/subcontractors", label: "Subcontractors", hint: "Approved subcontractors, their insurance, their certificates and their agreements" },
          { to: "/office/training", label: "Training sessions", hint: "On-site training delivered to a customer's staff, with attendees and the certificate each completed session issues" },
        ],
      },
      {
        to: "/office/compliance",
        label: "Compliance",
        icon: "CP",
        hint: "Records, registers and the packs ELE sends out. Tabs: Overview, Compliance reports, Training documents. Accidents, complaints, enforcement notices, environmental records, internal audits and management reviews are cards on the Overview",
        tabs: [
          {
            to: "/office/compliance",
            label: "Overview",
            hint: "The compliance hub: health signals, and a card for every compliance register and document store: complaints, accidents and incidents, enforcement notices and prosecutions, environmental records, document library, internal audits and management reviews",
            also: [
              "/office/complaints",
              "/office/accidents",
              "/office/enforcement-actions",
              "/office/environmental-records",
              "/office/environmental-objectives",
              "/office/compliance/documents",
              "/office/compliance/audits",
              "/office/compliance/management-reviews",
            ],
          },
          { to: "/office/compliance-reports", label: "Compliance reports", hint: "Build the compliance packs ELE sends out for accreditation and client procurement. Previously called Auditor exports" },
          { to: "/office/training-documents", label: "Training documents", hint: "The reference material behind each training type: on-site procedures a customer's staff follows, plus internal-only procedures. Review, version and send them to customers" },
        ],
      },
    ],
  },
  {
    label: "Internal",
    items: [
      {
        to: "/office/sla-report",
        label: "Reports",
        icon: "RS",
        hint: "Internal figures for running Guvnor, never sent to anyone. Tabs: Call-out response, Quote conversion, Lift performance, Customer satisfaction figures",
        tabs: [
          { to: "/office/sla-report", label: "Call-out response", hint: "How quickly call-outs were attended against the promised response times, by customer, site and area" },
          { to: "/office/quote-analytics", label: "Quote conversion", hint: "How quotes convert: the sent-to-signed funnel, win and loss reasons, and the ARR pipeline" },
          { to: "/office/lift-performance", label: "Lift performance", hint: "Cost, charge and margin per lift, breakdown frequency and repeat visits" },
          { to: "/office/nps-figures", label: "Customer satisfaction figures", hint: "Net Promoter Score: how likely customers are to recommend ELE, from the post-service surveys. Previously called Customer satisfaction or NPS", also: ["/office/nps"] },
        ],
      },
      {
        // One menu item. The Settings tab is itself a short list of sections, one on screen at a time
        // (Session SC7B, src/lib/settingsSections.ts); the Admin pages sit beside it as tabs, each with
        // its own admin-only rule unchanged. Every section address keeps this hub and tab lit (`also`),
        // and the menu search finds a section by the names of its cards (settingsSections.ts hints).
        to: "/office/settings",
        label: "Settings",
        icon: "SE",
        hint: "Company settings, integrations, database backup, recent texts, and the admin pages. Tabs: Settings, Office users, Staff activity, Auditor access, and for an admin the other admin pages. Desktop notifications are the bell icon in the header",
        tabs: [
          { to: "/office/settings", label: "Settings", hint: "Company settings, integrations, database backup, and recent texts. Desktop notifications are the bell icon in the header", also: settingsSectionPaths() },
          { to: "/office/office-users", label: "Office users", hint: "Office logins for your own staff" },
          { to: "/office/activity", label: "Staff activity", hint: "Who did what on the platform, newest first: filter by person, record type and date, and open the record. Read only" },
          { to: "/office/auditors", label: "Auditor access", hint: "Give an outside assessor a read-only login to the auditor portal" },
          { to: "/office/coverage-zones", label: "Coverage zones", hint: "How quickly ELE aims to attend a call-out in each postcode area (admin only)", adminOnly: true },
          { to: "/office/connected-ai", label: "Connected AI", hint: "Connect Claude or another AI assistant to the platform: generate and revoke connection tokens (admin only)", adminOnly: true },
          { to: "/office/personal-data", label: "Personal data requests", hint: "Find everything held about one person to answer a subject access or erasure request, and download a copy of their data (admin only)", adminOnly: true },
          { to: "/office/data-retention", label: "Data retention", hint: "Records past how long we keep them, proposed each week for deletion or anonymising: nothing is removed until you confirm (admin only)", adminOnly: true },
        ],
      },
    ],
  },
];

// The most top-level items the menu may ever hold. nav.test.ts fails if OFFICE_NAV has more.
export const OFFICE_NAV_MAX_ITEMS = 14;
export const OFFICE_NAV_GROUP_COUNT = 4;


// Every office path the sidebar links to (used by the layout's active-state logic
// and by tests that guard against duplicate or off-tree routes). These are the TOP-LEVEL items only.
export function officeNavPaths(): string[] {
  return OFFICE_NAV.flatMap((section) => section.items.map((i) => i.to));
}

/** The path part of a nav `to` (any query dropped). */
export function navPathOf(to: string): string {
  const i = to.indexOf("?");
  return i === -1 ? to : to.slice(0, i);
}

// Every tab path inside the hubs, query dropped, no duplicates. Each must resolve to a registered
// route (App.routes.test.tsx), exactly as the top-level paths must.
export function officeNavTabPaths(): string[] {
  const out = new Set<string>();
  for (const section of OFFICE_NAV) for (const item of section.items) for (const tab of item.tabs ?? []) out.add(navPathOf(tab.to));
  return [...out];
}

/** A nav entry the menu can search or pin: a top-level item, or one tab of a hub. */
export interface NavEntry extends NavItem {
  /** The group the entry lives in. */
  section: string;
  /** For a tab: the label of the top-level item that holds it. */
  hub?: string;
}

/**
 * Every top-level item and every tab (the first tab, which shares its hub's path, only when
 * `includeFirstTab`), flattened in menu order. Used by the menu search and the dashboard's pinnable
 * shortcuts, so a page that moved into a hub is still found and still pinnable. Pure.
 */
export function officeNavEntries(sections: NavSection[] = OFFICE_NAV, includeFirstTab = false): NavEntry[] {
  const out: NavEntry[] = [];
  for (const section of sections) {
    for (const item of section.items) {
      out.push({ ...item, tabs: undefined, section: section.label });
      (item.tabs ?? []).forEach((tab, i) => {
        if (i === 0 && !includeFirstTab) return;
        out.push({
          to: tab.to,
          label: tab.label,
          icon: item.icon,
          hint: tab.hint,
          adminOnly: tab.adminOnly || item.adminOnly || undefined,
          section: section.label,
          hub: item.label,
        });
      });
    }
  }
  return out;
}

// ---- Which hub and tab a location belongs to (Session SC7A) ----

/** True when `pathname` is `base` or lies under it at a "/" boundary. The office root matches only itself. */
export function pathBelongs(pathname: string, base: string): boolean {
  const p = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (base === "/office") return p === "/office";
  return p === base || p.startsWith(base + "/");
}

/**
 * How well a location matches one tab: -1 for no match, else a score where a longer path wins and,
 * among tabs on the same path, the one whose named query keys all match wins (so Ready to send beats
 * Awaiting release when ?stage=ready_to_send is present, and loses when it is not). Pure.
 */
export function tabMatchScore(tab: Pick<NavTab, "to" | "also">, pathname: string, search = ""): number {
  const params = new URLSearchParams(search);
  let best = -1;
  for (const candidate of [tab.to, ...(tab.also ?? [])]) {
    const qi = candidate.indexOf("?");
    const path = qi === -1 ? candidate : candidate.slice(0, qi);
    if (!pathBelongs(pathname, path)) continue;
    let queryScore = 0;
    let ok = true;
    if (qi !== -1) {
      for (const [k, v] of new URLSearchParams(candidate.slice(qi + 1))) {
        if (params.get(k) === v) queryScore += 1;
        else ok = false;
      }
    }
    if (!ok) continue;
    best = Math.max(best, path.length * 100 + queryScore);
  }
  return best;
}

export interface HubMatch {
  section: NavSection;
  item: NavItem;
  /** The tabs of the hub (a plain item has none). */
  tabs: NavTab[];
  /** Index of the active tab within `tabs` (0 for a plain item). */
  tabIndex: number;
  /** True when the location IS a tab's page (or one of its `also` pages) rather than a record under it. */
  atRoot: boolean;
}

/** The hub (top-level item) a location belongs to, with its active tab, or null. Longest path wins. Pure. */
export function hubForLocation(sections: NavSection[], pathname: string, search = ""): HubMatch | null {
  let best: HubMatch | null = null;
  let bestScore = -1;
  for (const section of sections) {
    for (const item of section.items) {
      const tabs = item.tabs ?? [];
      const candidates: NavTab[] = tabs.length > 0 ? tabs : [{ to: item.to, label: item.label }];
      candidates.forEach((tab, tabIndex) => {
        const score = tabMatchScore(tab, pathname, search);
        if (score > bestScore) {
          bestScore = score;
          const p = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
          const roots = [tab.to, ...(tab.also ?? [])].map(navPathOf);
          best = { section, item, tabs, tabIndex, atRoot: roots.includes(p) };
        }
      });
    }
  }
  return best;
}

/** True when `item`'s hub (any of its tabs) holds this location. Pure. */
export function navItemActive(item: NavItem, pathname: string, search = ""): boolean {
  const tabs = item.tabs ?? [];
  const candidates: NavTab[] = tabs.length > 0 ? tabs : [{ to: item.to }] as NavTab[];
  return candidates.some((t) => tabMatchScore(t, pathname, search) >= 0);
}

/** The tabs a role may see (an adminOnly tab is hidden from the office role). Pure. */
export function visibleTabsForRole(item: NavItem, role: Role | undefined): NavTab[] {
  return (item.tabs ?? []).filter((t) => role === "admin" || !t.adminOnly);
}

// Per-page browser tab titles (Session AQS, item 18: "One map in the shell"). Every office route
// showed the same static "ELE-Guvnor" title (index.html's own <title>, never overwritten), so a
// dozen open tabs were indistinguishable. One flat map, built from OFFICE_NAV's own labels (the hub
// items and every tab) rather than a second hand-typed list, so a renamed nav item's tab title cannot
// drift from its sidebar label. A tab's own label wins on its path (Sites, not Customers).
const OFFICE_NAV_TITLES: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const section of OFFICE_NAV) {
    for (const item of section.items) {
      map[item.to] = item.label;
      for (const tab of item.tabs ?? []) {
        const p = navPathOf(tab.to);
        // The first tab on a path names it; "Ready to send" (same path as "Awaiting release") never overwrites.
        if (!(p in map) || p === item.to) map[p] = tab.label;
      }
    }
  }
  return map;
})();

/**
 * The browser tab title for an office pathname (e.g. "/office/customers/abc123"), for
 * `document.title`. A route with no nav item of its own (every `:id` detail page, `quotes/new`,
 * a redirect target) falls back to the LONGEST matching nav path that is a PREFIX of it, so a
 * customer detail page reads "Customers" rather than the bare app name; a route under no nav path
 * at all (the dashboard index, `/office`) falls back to the bare app name. Pure: takes the
 * pathname, not the router, so it is unit-testable with no DOM.
 */
export function officeTabTitle(pathname: string): string {
  const exact = OFFICE_NAV_TITLES[pathname];
  if (exact) return exact;
  let best: string | null = null;
  for (const path of Object.keys(OFFICE_NAV_TITLES)) {
    if (pathname === path || pathname.startsWith(`${path}/`)) {
      if (!best || path.length > best.length) best = path;
    }
  }
  return best ? OFFICE_NAV_TITLES[best]! : "ELE-Guvnor";
}

// ---- Custom (per-user) nav order ----
// The Director can drag-reorder the sidebar. The order is stored as a flat list of nav
// `to` paths and applied WITHIN each section only -- items never change section, so the
// route-to-section mapping (and nav.test) is preserved. This is a presentation layer
// over OFFICE_NAV: it never adds, drops or moves an item between sections.

// The flat list of `to` paths in the given sections' current order (what gets saved
// after a drag).
export function flattenNavOrder(sections: NavSection[]): string[] {
  return sections.flatMap((s) => s.items.map((i) => i.to));
}

// Apply a saved custom order to the nav sections, reordering each section's items by the
// saved order. Items present in the saved order come first (in that order); any item NOT
// in the saved order (e.g. a newly added route) keeps its default relative order after
// them; a saved key that no longer maps to a real item is ignored. Degrades to the
// default order when the saved order is null/empty. Never drops an item or changes its
// section. Pure.
export function applyNavOrder(sections: NavSection[], order: string[] | null | undefined): NavSection[] {
  if (!order || order.length === 0) return sections;
  const rank = new Map<string, number>();
  order.forEach((to, i) => {
    if (!rank.has(to)) rank.set(to, i); // first occurrence wins; ignore dupes
  });
  return sections.map((section) => {
    // Stable sort: ranked items by rank; unranked items keep their default relative
    // order (Number.POSITIVE_INFINITY ties resolve to 0 -> stable).
    const items = section.items
      .map((item, i) => ({ item, i, rank: rank.has(item.to) ? rank.get(item.to)! : Number.POSITIVE_INFINITY }))
      .sort((a, b) => a.rank - b.rank || a.i - b.i)
      .map((x) => x.item);
    return { ...section, items };
  });
}

// Move a nav item within its section in response to a drag (the active item dropped over
// another). Both ids are item `to` paths. A cross-section drop is ignored (an item never
// changes section). Returns the new sections and whether anything changed. Pure (no
// dnd-kit dependency), so the drag outcome -- and thus the persisted order
// (flattenNavOrder of the result) -- is unit-testable without simulating a real drag.
export function reorderSectionsWithin(
  sections: NavSection[],
  activeId: string,
  overId: string,
): { sections: NavSection[]; changed: boolean } {
  if (activeId === overId) return { sections, changed: false };
  let changed = false;
  const next = sections.map((section) => {
    const fromIdx = section.items.findIndex((i) => i.to === activeId);
    const toIdx = section.items.findIndex((i) => i.to === overId);
    if (fromIdx === -1 || toIdx === -1) return section; // not this section / cross-section -> ignore
    changed = true;
    const items = section.items.slice();
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved!);
    return { ...section, items };
  });
  return changed ? { sections: next, changed } : { sections, changed };
}

// ---- Fully-flexible nav layout (Session AD, Item 4) ----
// The within-section order above (applyNavOrder / a flat list of `to` paths) is superseded for the
// office sidebar by a STRUCTURED layout that lets an item move to ANY section and lets whole groups
// be reordered. The layout is an ordered list of sections, each an ordered list of item `to` paths:
//
//   [{ label: "Servicing", items: ["/office/jobsheets", "/office/defects", "/office/knowledge"] }, ...]
//
// The example shows the SHAPE, not a mirror of OFFICE_NAV: a saved layout is a user's own
// arrangement and diverges from the default the moment either side moves. It is also a FULL
// snapshot (flattenNavLayout writes every group and item), so it records placements the user never
// chose, and it therefore hides a later regroup from anyone who has ever dragged. See the test
// "a SAVED custom layout hides a regroup and leaves a new group empty" in nav.test.ts.
//
// It is stored in User.navOrder (a Json column, so no migration). The old flat string[] format still
// loads (applyNavOrder, within-section) until the user's next drag, which saves the new layout, so a
// saved within-section order is never lost. OFFICE_NAV remains the source of truth for item
// definitions (label/icon/end) and section labels; the layout only carries placement + order.

export interface NavLayoutSection {
  label: string;
  items: string[]; // item `to` paths, in order
}
export type NavLayout = NavLayoutSection[];

// The sortable id for a group heading. Items use their `to` path as the id; groups use this prefixed
// form so a drag-end can tell a group move from an item move. A `to` path always starts with "/",
// so the "group:" prefix can never collide with an item id.
export const NAV_GROUP_ID_PREFIX = "group:";
export function navGroupSortId(label: string): string {
  return `${NAV_GROUP_ID_PREFIX}${label}`;
}
export function isNavGroupSortId(id: string): boolean {
  return id.startsWith(NAV_GROUP_ID_PREFIX);
}
function navGroupLabelFromId(id: string): string {
  return id.slice(NAV_GROUP_ID_PREFIX.length);
}

// The layout to persist after a drag: the sections' current order, each with its items' `to` paths.
export function flattenNavLayout(sections: NavSection[]): NavLayout {
  return sections.map((s) => ({ label: s.label, items: s.items.map((i) => i.to) }));
}

// Apply a saved structured layout over the canonical OFFICE_NAV sections. The result places each
// known item in the section + position the layout records, then GRACEFULLY handles drift: a saved
// `to` that no longer maps to a real item is dropped; a saved label that no longer maps to a real
// section is dropped; a real section missing from the layout is appended (keeping its default
// items); a real item missing from the layout is appended to its DEFAULT section. So a new route or
// a new group added to OFFICE_NAV after the user saved is never lost, and an item never appears
// twice. Degrades to the default order when the layout is null/empty. Pure.
export function applyNavLayout(sections: NavSection[], layout: NavLayout | null | undefined): NavSection[] {
  if (!layout || layout.length === 0) return sections;
  const itemByTo = new Map<string, NavItem>();
  for (const s of sections) for (const i of s.items) itemByTo.set(i.to, i);
  const validLabels = new Set(sections.map((s) => s.label));

  const placed = new Set<string>();
  const result: NavSection[] = [];
  const resultByLabel = new Map<string, NavSection>();

  // 1. Build from the saved layout, in saved section + item order.
  for (const saved of layout) {
    if (!validLabels.has(saved.label) || resultByLabel.has(saved.label)) continue; // drop renamed/duplicate
    const items: NavItem[] = [];
    for (const to of saved.items) {
      if (placed.has(to)) continue; // ignore a duplicate `to`
      const item = itemByTo.get(to);
      if (!item) continue; // drop a removed route
      placed.add(to);
      items.push(item);
    }
    const sec: NavSection = { label: saved.label, items };
    result.push(sec);
    resultByLabel.set(saved.label, sec);
  }
  // 2. Append any real section missing from the saved layout (a newly-added group), in default order.
  for (const def of sections) {
    if (!resultByLabel.has(def.label)) {
      const sec: NavSection = { label: def.label, items: [] };
      result.push(sec);
      resultByLabel.set(def.label, sec);
    }
  }
  // 3. Append any real item not placed by the layout (a newly-added route) to its DEFAULT section.
  for (const def of sections) {
    for (const item of def.items) {
      if (!placed.has(item.to)) {
        placed.add(item.to);
        resultByLabel.get(def.label)!.items.push(item);
      }
    }
  }
  return result;
}

// Migrate a legacy flat within-section order (string[] of `to` paths) to the structured layout, by
// applying it over OFFICE_NAV (which keeps each item in its default section) and flattening the
// result. Lossless for the within-section order a user already saved. Pure.
export function migrateNavOrderToLayout(sections: NavSection[], order: string[] | null | undefined): NavLayout {
  return flattenNavLayout(applyNavOrder(sections, order));
}

function sectionLabelContaining(sections: NavSection[], to: string): string | null {
  for (const s of sections) if (s.items.some((i) => i.to === to)) return s.label;
  return null;
}

// The new sections after a drag-end, supporting BOTH an item moved to any position in any section
// AND a whole group moved. `activeId` / `overId` are dnd-kit ids: an item is its `to` path, a group
// is navGroupSortId(label). Rules:
//   - active is a GROUP: move the whole section to the target group's position (over may be a group
//     heading or an item, whose section is the target). A group never moves into itself.
//   - active is an ITEM, over is an ITEM: move the item to just before the over item, in the over
//     item's section (cross-section allowed).
//   - active is an ITEM, over is a GROUP heading: move the item to the TOP of that group (so dropping
//     onto a collapsed group's heading still works).
// Returns { sections, changed }. Pure (no dnd-kit dependency), so the persisted layout
// (flattenNavLayout of the result) is unit-testable without simulating a real drag.
export function applyNavDragEnd(
  sections: NavSection[],
  activeId: string,
  overId: string,
): { sections: NavSection[]; changed: boolean } {
  if (activeId === overId) return { sections, changed: false };

  if (isNavGroupSortId(activeId)) {
    const activeLabel = navGroupLabelFromId(activeId);
    const targetLabel = isNavGroupSortId(overId) ? navGroupLabelFromId(overId) : sectionLabelContaining(sections, overId);
    if (!targetLabel || targetLabel === activeLabel) return { sections, changed: false };
    const fromIdx = sections.findIndex((s) => s.label === activeLabel);
    const toIdx = sections.findIndex((s) => s.label === targetLabel);
    if (fromIdx === -1 || toIdx === -1) return { sections, changed: false };
    const next = sections.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved!); // arrayMove semantics: insert at the target index after removal
    return { sections: next, changed: true };
  }

  // active is an item. Resolve the target section + the insert index FROM THE ORIGINAL arrays
  // (before removing the active item), then remove and re-insert at that index -- dnd-kit's arrayMove
  // semantics, matching the legacy reorderSectionsWithin. Computing the index AFTER removal would
  // land a downward same-group drag one slot short (Session AD review fix).
  if (sectionLabelContaining(sections, activeId) === null) return { sections, changed: false };
  let targetLabel: string;
  let insertIdx: number;
  if (isNavGroupSortId(overId)) {
    targetLabel = navGroupLabelFromId(overId);
    insertIdx = 0; // dropped on a group heading -> top of that group
  } else {
    const tl = sectionLabelContaining(sections, overId);
    if (tl === null) return { sections, changed: false };
    targetLabel = tl;
    insertIdx = sections.find((s) => s.label === targetLabel)!.items.findIndex((i) => i.to === overId);
  }

  const next = sections.map((s) => ({ ...s, items: s.items.slice() }));
  let moved: NavItem | undefined;
  for (const s of next) {
    const idx = s.items.findIndex((i) => i.to === activeId);
    if (idx >= 0) {
      [moved] = s.items.splice(idx, 1);
      break;
    }
  }
  if (!moved) return { sections, changed: false };
  // For a same-section move, removing the active item already shifted the array; splicing at the
  // original over index (arrayMove) lands the item AT the drop target, not one short.
  next.find((s) => s.label === targetLabel)!.items.splice(insertIdx, 0, moved);
  return { sections: next, changed: true };
}

// ---- Collapsible group state ----
// The office sidebar groups collapse/expand. State is the set of section labels the user has
// expanded. D28 (Session AQT): navigating INTO a section auto-opens it (reconcileExpandedOnNavigate,
// below) and the choice then persists per user via GET/PUT /api/me/nav-order's expandedGroups
// (OfficeLayout.tsx wires this up; sessionStorage is only that component's synchronous first-paint
// cache). This is NOT the same as force-pinning the active section open -- see isSectionOpen's own
// comment for the bug that distinction avoids repeating.

// Which section currently contains the active route: the hub whose tab (or `also` page) matches by
// longest path (Session SC7A: a hub holds many pages, so a record under any of them lights its hub).
// The office root (the dashboard) matches only itself. Null if none match. Pure -- the section order
// is irrelevant (it scans every item).
export function activeSectionLabel(sections: NavSection[], pathname: string, search = ""): string | null {
  return hubForLocation(sections, pathname, search)?.section.label ?? null;
}

// A section is open ONLY if the user has expanded it. The group containing the active route
// is NO LONGER force-open: that made the active section impossible to collapse (you are on a
// Portfolio page, so Portfolio refuses to close -- the reported "Portfolio cannot collapse"
// bug, which was really "the active section cannot collapse"). Every section now starts
// collapsed and the user opens what they need. Pure.
export function isSectionOpen(label: string, expanded: ReadonlySet<string>): boolean {
  return expanded.has(label);
}

// Toggle a section's expanded state, returning a NEW set. Pure.
export function toggleGroup(expanded: ReadonlySet<string>, label: string): Set<string> {
  const next = new Set(expanded);
  if (next.has(label)) next.delete(label);
  else next.add(label);
  return next;
}

// D28's badge rollup (Director-decided 23 September 2026, built Session AQW): a per-item badge
// (e.g. the Projects active-count badge) is clipped out of view the moment its group collapses,
// which is the exact case a rollup exists for. Sums whatever badges the section's own items carry
// so a closed group can still show that something inside it needs attention. Pure.
export function groupBadgeTotal(items: readonly NavItem[], badges: Record<string, number>): number {
  return items.reduce((sum, item) => sum + navItemBadge(item, badges), 0);
}

// SC7A: a hub's badge is the sum of the badges its pages carry (the Projects count sits on the Repairs
// hub now that Projects is one of its tabs), each counted once. Pure.
export function navItemBadge(item: NavItem, badges: Record<string, number>): number {
  const seen = new Set<string>();
  let total = 0;
  for (const to of [item.to, ...(item.tabs ?? []).map((t) => navPathOf(t.to))]) {
    if (seen.has(to)) continue;
    seen.add(to);
    total += badges[to] ?? 0;
  }
  return total;
}

// When the active section changes by navigation, make sure the newly-active section is open.
// SC7A: the old accordion (open the new group, CLOSE the previous one) is gone. With four groups
// that all start open, closing the group you just left would take away the other half of the menu
// every time you changed group, which is more presses, not fewer. A group the person closed stays
// closed until they navigate into it; one they left open stays open. No change when navigating within
// the same section or into a section that is already expanded. Returns { next, changed } so the caller
// can skip a redundant save. Pure.
export function reconcileExpandedOnNavigate(
  expanded: ReadonlySet<string>,
  prevActive: string | null,
  nextActive: string | null,
): { next: Set<string>; changed: boolean } {
  if (!nextActive || nextActive === prevActive || expanded.has(nextActive)) {
    return { next: new Set(expanded), changed: false };
  }
  const next = new Set(expanded);
  next.add(nextActive);
  return { next, changed: true };
}

// Coerce a stored/loaded list of expanded-group labels to the subset that still maps to a
// real section (a renamed/removed group is dropped). Defensive + pure.
export function coerceExpandedGroups(raw: string[] | null | undefined, sections: NavSection[]): string[] {
  if (!Array.isArray(raw)) return [];
  const valid = new Set(sections.map((s) => s.label));
  return raw.filter((label) => typeof label === "string" && valid.has(label));
}

// P-1, Session AQT: the menu shown to an office-role login omits an adminOnly item (its own page
// already refuses a non-admin; this stops the menu offering a dead-end link to it -- P-2 covers
// what a non-admin sees if they still reach one directly, e.g. a stale bookmark). admin sees
// everything. A section left with no items after filtering is dropped too, so an all-admin-only
// group never renders as an empty heading. Pure.
export function visibleNavForRole(sections: NavSection[], role: Role | undefined): NavSection[] {
  if (role === "admin") return sections;
  return sections
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => !item.adminOnly)
        .map((item) => (item.tabs ? { ...item, tabs: item.tabs.filter((t) => !t.adminOnly) } : item)),
    }))
    .filter((section) => section.items.length > 0);
}
